import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, MutationCache, QueryCache } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Loader2 } from "lucide-react";

// ── Eager (needed on first paint / auth redirect) ──────────────────────────
import Index from "./pages/Index";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import DoctorLogin from "./pages/DoctorLogin";
import DoctorSignup from "./pages/DoctorSignup";
import DoctorSetup from "./pages/DoctorSetup";
import NotFound from "./pages/NotFound";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";

// ── Lazy (code-split — loaded on demand) ──────────────────────────────────
const Assessment          = lazy(() => import("./pages/Assessment"));
const ClinicalAssessment  = lazy(() => import("./pages/ClinicalAssessment"));
const DocumentAnalysis    = lazy(() => import("./pages/DocumentAnalysis"));
const Live                = lazy(() => import("./pages/Live"));
const VoiceAI             = lazy(() => import("./pages/VoiceAI"));
const Analytics           = lazy(() => import("./pages/Analytics"));
const Settings            = lazy(() => import("./pages/Settings"));
const Consultation        = lazy(() => import("./pages/Consultation"));
const OperationalManual   = lazy(() => import("./pages/OperationalManual"));
const Dashboard           = lazy(() => import("./pages/Dashboard"));
const PatientOnboarding   = lazy(() => import("./pages/PatientOnboarding"));
const Founders            = lazy(() => import("./pages/Founders"));
const Admin               = lazy(() => import("./pages/Admin"));
const FounderCommandCenter = lazy(() => import("./pages/FounderCommandCenter"));

// Consultation sub-components
const DoctorConsole       = lazy(() => import("./components/consultation/DoctorConsole"));
const AdminAuditConsole   = lazy(() => import("./components/admin/AdminAuditConsoleSecure"));

// Admin Command Center (separate chunk per page)
const AdminLayout         = lazy(() => import("./components/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminDashboard      = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminDoctors        = lazy(() => import("./pages/admin/AdminDoctors"));
const AdminPatients       = lazy(() => import("./pages/admin/AdminPatients"));
const AdminConsultations  = lazy(() => import("./pages/admin/AdminConsultations"));
const AdminAISystems      = lazy(() => import("./pages/admin/AdminAISystems"));
const AdminAssessments    = lazy(() => import("./pages/admin/AdminAssessments"));
const AdminPayments       = lazy(() => import("./pages/admin/AdminPayments"));
const AdminMedicalRecords = lazy(() => import("./pages/admin/AdminMedicalRecords"));
const AdminAuditLogs      = lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminCalls          = lazy(() => import("./pages/admin/AdminCalls"));
const AdminNotifications  = lazy(() => import("./pages/admin/AdminNotifications"));
const LiveMonitoring      = lazy(() => import("./pages/admin/LiveMonitoring"));

// ── Loading fallback ───────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    </div>
  );
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Request failed";
      toast({
        title: "Request failed",
        description: message,
        variant: "destructive",
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Operation failed";
      toast({
        title: "Operation failed",
        description: message,
        variant: "destructive",
      });
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* ── Public ──────────────────────────────────────────── */}
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />

              {/* ── Doctor Auth ──────────────────────────────────────── */}
              <Route path="/doctor/login" element={<DoctorLogin />} />
              <Route path="/doctor/signup" element={<DoctorSignup />} />
              <Route path="/doctor-setup" element={<ProtectedRoute><DoctorSetup /></ProtectedRoute>} />

              {/* ── Patient ──────────────────────────────────────────── */}
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/onboarding" element={<ProtectedRoute><PatientOnboarding /></ProtectedRoute>} />
              <Route path="/assessment" element={<ProtectedRoute><Assessment /></ProtectedRoute>} />
              <Route path="/clinical-assessment" element={<ProtectedRoute><ClinicalAssessment /></ProtectedRoute>} />
              <Route path="/document-analysis" element={<ProtectedRoute><DocumentAnalysis /></ProtectedRoute>} />
              <Route path="/doctor" element={<Navigate to="/consultation" replace />} />

              {/* ── Admin Command Center ─────────────────────────────── */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requireAdmin>
                    <ErrorBoundary label="AdminLayout">
                      <AdminLayout />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="assessments" element={<AdminAssessments />} />
                <Route path="doctors" element={<AdminDoctors />} />
                <Route path="patients" element={<AdminPatients />} />
                <Route path="consultations" element={<AdminConsultations />} />
                <Route path="ai-systems" element={<AdminAISystems />} />
                <Route path="calls" element={<AdminCalls />} />
                <Route path="medical-records" element={<AdminMedicalRecords />} />
                <Route path="payments" element={<AdminPayments />} />
                <Route path="notifications" element={<AdminNotifications />} />
                <Route path="audit-logs" element={<AdminAuditLogs />} />
                <Route path="audit" element={<AdminAuditConsole />} />
                <Route path="live" element={<LiveMonitoring />} />
                <Route path="legacy" element={<Admin />} />
              </Route>

              {/* ── Feature pages ────────────────────────────────────── */}
              <Route path="/live" element={<ProtectedRoute><Live /></ProtectedRoute>} />
              <Route path="/voice-ai" element={<ProtectedRoute><VoiceAI /></ProtectedRoute>} />
              <Route path="/analytics" element={<ProtectedRoute requireAdmin><Analytics /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/consultation" element={<ProtectedRoute><Consultation /></ProtectedRoute>} />
              <Route
                path="/consultation/:consultationId"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary label="ConsultationPage">
                      <Consultation />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route path="/operational-manual" element={<ProtectedRoute requireAdmin><OperationalManual /></ProtectedRoute>} />

              {/* ── Redirects ────────────────────────────────────────── */}
              <Route path="/doctor/prescriptions" element={<Navigate to="/doctor" replace />} />
              <Route path="/patient/reports" element={<Navigate to="/consultation" replace />} />

              {/* ── Founder Command Center (self-gated) ──────────────── */}
              <Route path="/founders" element={<Founders />} />
              <Route path="/founder-command-center" element={<ProtectedRoute requireAdmin><FounderCommandCenter /></ProtectedRoute>} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
